import { useGridApiRef } from '@mui/x-data-grid-premium';
import { useMemo, useState } from 'react';
import { AgButton } from 'src/components/ui';
import useGridExport from 'src/hooks/useGridExport';
import { DataGrid, GridToolbarV2, useGridRows } from 'src/modules/grids';
import CustomerGroupDialog from './CustomerGroupDialog';
import { CustomerGroupsMeta } from './constants';
import useCustomerGroupsColumns from './hooks/useCustomerGroupColumns';
import useCustomerGroups, { CUSTOMER_GROUPS_GRID_STORAGE_KEY } from './hooks/useCustomerGroups';

/** Customer Groups, one server page at a time (TAS-3660 P7 BZ75); a failed read shows Retry through `reader`. */
export default function CustomerGroups() {
  const apiRef = useGridApiRef();
  const enhancedGridState = useGridRows({ mode: 'paged', storageKey: CUSTOMER_GROUPS_GRID_STORAGE_KEY });
  const { groups, loading, pageInfo, totalCount, fetchAllForExport, reader } = useCustomerGroups(enhancedGridState);
  // The header Export button (#7627 F20) writes every group under the current search and filters.
  useGridExport(apiRef, 'customer-groups', undefined, undefined, fetchAllForExport);
  const [editGroup, setEditGroup] = useState<Partial<CustomerGroupsMeta>>();
  const columns = useCustomerGroupsColumns(setEditGroup);

  const toolbar = useMemo(
    () => (
      <GridToolbarV2
        apiRef={apiRef}
        enhancedGridState={enhancedGridState}
        entityName="Customer Group"
        entityNamePlural="Customer Groups"
        toolbarActionsRight={
          <AgButton variant="primary" onPress={() => setEditGroup({})}>
            Add Customer Group
          </AgButton>
        }
      />
    ),
    [apiRef, enhancedGridState]
  );

  return (
    <>
      {reader}
      {editGroup && <CustomerGroupDialog group={editGroup} close={() => setEditGroup(undefined)} />}
      <DataGrid
        mode="paged"
        rows={groups}
        totalCount={totalCount}
        pageInfo={pageInfo}
        loading={loading}
        enhancedGridState={enhancedGridState}
        columns={columns}
        gridKey="settings.business.customer-groups"
        storageKey={CUSTOMER_GROUPS_GRID_STORAGE_KEY}
        apiRef={apiRef}
        slots={{ toolbar }}
        variant="list"
      />
    </>
  );
}
